export const CommandOptionType = {
  Subcommand: 1,
  SubcommandGroup: 2,
  String: 3,
  Integer: 4,
  Boolean: 5,
  User: 6,
  Channel: 7,
  Role: 8,
  Mentionable: 9,
  Number: 10,
  Attachment: 11,
} as const;

export interface ApplicationCommandOption {
  type: number;
  name: string;
  description?: string;
  required?: boolean;
  choices?: Array<{ name: string; value: string | number }>;
  options?: ApplicationCommandOption[];
  autocomplete?: boolean;
}

export interface ApplicationCommand {
  id: string;
  application_id: string;
  type?: number;
  name: string;
  description?: string;
  options?: ApplicationCommandOption[];
  application?: { id?: string; name?: string; avatar?: string | null };
}

export interface SlashChoice {
  id: string;
  label: string;
  value: string | number | boolean;
  type?: number;
}

export interface SlashCommandDraft {
  command: ApplicationCommand;
  path: string[];
  values: Record<string, string | number | boolean>;
  labels: Record<string, string>;
  focused: string;
  attachmentUnsupported?: string | null;
}

export interface SlashOptionSources {
  members?: Array<{ id?: string; user_id?: string; username?: string; user?: { username?: string | null } }>;
  channels?: Array<{ id: string; name: string; type?: string }>;
  roles?: Array<{ id: string; name: string; color?: string }>;
}

export function createSlashDraft(command: ApplicationCommand): SlashCommandDraft {
  const options = getEffectiveOptions(command, []);
  return {
    command,
    path: [],
    values: {},
    labels: {},
    focused: options.find((option) => option.required)?.name || options[0]?.name || '',
    attachmentUnsupported: null,
  };
}

export function getSubcommandChoices(command: ApplicationCommand, path: string[] = []): ApplicationCommandOption[] {
  const roots = command.options || [];
  if (path.length === 0) {
    return roots.filter((option) => option.type === CommandOptionType.Subcommand || option.type === CommandOptionType.SubcommandGroup);
  }
  const group = roots.find((option) => option.type === CommandOptionType.SubcommandGroup && option.name === path[0]);
  if (group && path.length === 1) {
    return (group.options || []).filter((option) => option.type === CommandOptionType.Subcommand);
  }
  return [];
}

export function setSubcommandPath(draft: SlashCommandDraft, option: ApplicationCommandOption): SlashCommandDraft {
  const nextPath = option.type === CommandOptionType.SubcommandGroup
    ? [option.name]
    : draft.path.length === 1 && getSubcommandChoices(draft.command, [draft.path[0]]).length > 0
      ? [draft.path[0], option.name]
      : [option.name];
  const effective = getEffectiveOptions(draft.command, nextPath);
  return {
    ...draft,
    path: nextPath,
    values: {},
    labels: {},
    focused: effective.find((item) => item.required)?.name || effective[0]?.name || '',
  };
}

export function getEffectiveOptions(command: ApplicationCommand, path: string[]): ApplicationCommandOption[] {
  let options = command.options || [];
  if (path.length > 0) {
    const first = options.find((option) => option.name === path[0]);
    if (first?.type === CommandOptionType.SubcommandGroup) {
      const sub = (first.options || []).find((option) => option.name === path[1]);
      options = sub?.options || [];
    } else if (first?.type === CommandOptionType.Subcommand) {
      options = first.options || [];
    }
  }
  return options.filter((option) => option.type !== CommandOptionType.Subcommand && option.type !== CommandOptionType.SubcommandGroup);
}

export function getFocusedOption(draft: SlashCommandDraft): ApplicationCommandOption | null {
  const options = getEffectiveOptions(draft.command, draft.path);
  return options.find((option) => option.name === draft.focused) || options[0] || null;
}

export function getNextFocusableOption(draft: SlashCommandDraft, currentName: string): ApplicationCommandOption | null {
  const options = getEffectiveOptions(draft.command, draft.path);
  const currentIndex = options.findIndex((option) => option.name === currentName);
  return options.slice(Math.max(currentIndex + 1, 0)).find((option) => draft.values[option.name] === undefined) || null;
}

export function getPreviousFilledOptionName(draft: SlashCommandDraft): string | null {
  const options = getEffectiveOptions(draft.command, draft.path);
  for (let index = options.length - 1; index >= 0; index -= 1) {
    const option = options[index];
    if (draft.values[option.name] !== undefined) return option.name;
  }
  return null;
}

export function setFocusedOption(draft: SlashCommandDraft, optionName: string): SlashCommandDraft {
  return { ...draft, focused: optionName };
}

export function setOptionValue(
  draft: SlashCommandDraft,
  option: ApplicationCommandOption,
  rawValue: string | number | boolean,
  label?: string,
): SlashCommandDraft {
  if (option.type === CommandOptionType.Attachment) {
    return { ...draft, focused: option.name, attachmentUnsupported: option.name };
  }

  const value = coerceOptionValue(option, rawValue);
  const options = getEffectiveOptions(draft.command, draft.path);
  const currentIndex = options.findIndex((item) => item.name === option.name);
  const nextFocus = options.slice(currentIndex + 1).find((item) => draft.values[item.name] === undefined)?.name || option.name;
  return {
    ...draft,
    values: { ...draft.values, [option.name]: value },
    labels: { ...draft.labels, [option.name]: label || String(rawValue) },
    focused: nextFocus,
    attachmentUnsupported: null,
  };
}

export function clearOptionValue(draft: SlashCommandDraft, optionName: string): SlashCommandDraft {
  const values = { ...draft.values };
  const labels = { ...draft.labels };
  delete values[optionName];
  delete labels[optionName];
  return { ...draft, values, labels, focused: optionName };
}

export function validateSlashDraft(draft: SlashCommandDraft): string | null {
  if (getSubcommandChoices(draft.command, draft.path).length > 0) {
    return 'Choose a subcommand.';
  }
  const missing = getEffectiveOptions(draft.command, draft.path).find((option) => option.required && draft.values[option.name] === undefined);
  if (missing) return `Missing required option: ${missing.name}`;
  if (draft.attachmentUnsupported) return `Attachment option "${draft.attachmentUnsupported}" is not supported here yet.`;
  return null;
}

export function validateOptionInput(option: ApplicationCommandOption, rawValue: string): string | null {
  const trimmed = rawValue.trim();
  if (option.type === CommandOptionType.Attachment) return `Attachment option "${option.name}" is not supported here yet.`;
  if (option.required && trimmed === '') return `${option.name} is required.`;
  if (trimmed === '') return null;
  if (option.type === CommandOptionType.Integer && !/^-?\d+$/.test(trimmed)) return `${option.name} must be an integer.`;
  if (option.type === CommandOptionType.Number && Number.isNaN(Number(trimmed))) return `${option.name} must be a number.`;
  if (option.choices?.length) {
    const exact = option.choices.some((choice) => String(choice.value) === trimmed || choice.name.toLowerCase() === trimmed.toLowerCase());
    if (!exact) return `Choose a valid value for ${option.name}.`;
  }
  return null;
}

export function optionAllowsFreeText(option: ApplicationCommandOption): boolean {
  return option.type === CommandOptionType.String || option.type === CommandOptionType.Integer || option.type === CommandOptionType.Number;
}

export function optionNeedsSelection(option: ApplicationCommandOption): boolean {
  return Boolean(option.choices?.length)
    || option.type === CommandOptionType.Boolean
    || option.type === CommandOptionType.User
    || option.type === CommandOptionType.Channel
    || option.type === CommandOptionType.Role
    || option.type === CommandOptionType.Mentionable;
}

export function serializeSlashDraft(draft: SlashCommandDraft): any[] {
  const leafOptions = getEffectiveOptions(draft.command, draft.path)
    .filter((option) => draft.values[option.name] !== undefined)
    .map((option) => ({ type: option.type, name: option.name, value: draft.values[option.name] }));

  if (draft.path.length === 2) {
    const group = draft.command.options?.find((option) => option.name === draft.path[0]);
    const sub = group?.options?.find((option) => option.name === draft.path[1]);
    return [{ type: CommandOptionType.SubcommandGroup, name: draft.path[0], options: [{ type: CommandOptionType.Subcommand, name: draft.path[1], options: leafOptions, ...(sub?.description ? { description: sub.description } : {}) }] }];
  }
  if (draft.path.length === 1) {
    return [{ type: CommandOptionType.Subcommand, name: draft.path[0], options: leafOptions }];
  }
  return leafOptions;
}

export function optionCandidates(
  option: ApplicationCommandOption,
  sources: SlashOptionSources,
  query = '',
): SlashChoice[] {
  const normalized = query.toLowerCase();
  const choiceValues = option.choices?.map((choice) => ({ id: String(choice.value), label: choice.name, value: choice.value, type: option.type })) || [];
  if (choiceValues.length > 0) return filterChoices(choiceValues, normalized);

  if (option.type === CommandOptionType.Boolean) {
    return filterChoices([
      { id: 'true', label: 'True', value: true, type: option.type },
      { id: 'false', label: 'False', value: false, type: option.type },
    ], normalized);
  }
  if (option.type === CommandOptionType.User || option.type === CommandOptionType.Mentionable) {
    const users = (sources.members || []).map((member) => ({
      id: member.user_id || member.id || '',
      label: member.user?.username || member.username || member.user_id || member.id || 'user',
      value: member.user_id || member.id || '',
      type: option.type,
    }));
    return filterChoices(users, normalized);
  }
  if (option.type === CommandOptionType.Channel) {
    return filterChoices((sources.channels || []).map((channel) => ({
      id: channel.id,
      label: `#${channel.name}`,
      value: channel.id,
      type: option.type,
    })), normalized);
  }
  if (option.type === CommandOptionType.Role) {
    return filterChoices((sources.roles || []).map((role) => ({
      id: role.id,
      label: `@${role.name}`,
      value: role.id,
      type: option.type,
    })), normalized);
  }
  return [];
}

export function displayOptionValue(draft: SlashCommandDraft, option: ApplicationCommandOption): string {
  if (draft.labels[option.name]) return draft.labels[option.name];
  const value = draft.values[option.name];
  return value === undefined ? '' : String(value);
}

function coerceOptionValue(option: ApplicationCommandOption, rawValue: string | number | boolean): string | number | boolean {
  if (option.type === CommandOptionType.Integer) return Number.parseInt(String(rawValue), 10);
  if (option.type === CommandOptionType.Number) return Number.parseFloat(String(rawValue));
  if (option.type === CommandOptionType.Boolean) return rawValue === true || String(rawValue).toLowerCase() === 'true';
  return rawValue;
}

function filterChoices(choices: SlashChoice[], normalized: string): SlashChoice[] {
  return choices.filter((choice) => !normalized || choice.label.toLowerCase().includes(normalized)).slice(0, 10);
}
